"use client";

import {
  Content as RadixAccordionContent,
  Header as RadixAccordionHeader,
  Item as RadixAccordionItem,
  Root as RadixAccordionRoot,
  Trigger as RadixAccordionTrigger,
  type AccordionContentProps as RadixAccordionContentProps,
  type AccordionHeaderProps as RadixAccordionHeaderProps,
  type AccordionItemProps as RadixAccordionItemProps,
  type AccordionMultipleProps as RadixAccordionMultipleProps,
  type AccordionSingleProps as RadixAccordionSingleProps,
  type AccordionTriggerProps as RadixAccordionTriggerProps,
} from "@radix-ui/react-accordion";
import { forwardRef } from "react";

import {
  accordionContentVariants,
  accordionHeadingVariants,
  accordionIconVariants,
  accordionItemVariants,
  accordionRootVariants,
  accordionTriggerVariants,
  type AccordionContentVariants,
  type AccordionHeadingVariants,
  type AccordionIconVariants,
  type AccordionItemVariants,
  type AccordionRootVariants,
  type AccordionTriggerVariants,
} from "./accordion.styles";
import { Icon, type IconProps, type IconRef } from "./icon";

// -----------------------------------------------------------------------------

export type AccordionRootRef = React.ComponentRef<typeof RadixAccordionRoot>;

export type AccordionRootProps = (
  | RadixAccordionSingleProps
  | RadixAccordionMultipleProps
) &
  AccordionRootVariants;

const AccordionRoot = forwardRef<AccordionRootRef, AccordionRootProps>(
  ({ className, itemDivider, itemSpacing, ...props }, ref) => (
    <RadixAccordionRoot
      {...props}
      className={accordionRootVariants({ className, itemDivider, itemSpacing })}
      ref={ref}
    />
  ),
);

AccordionRoot.displayName = "AccordionRoot";

export { AccordionRoot as Accordion };

// -----------------------------------------------------------------------------

export type AccordionItemRef = React.ComponentRef<typeof RadixAccordionItem>;

export interface AccordionItemProps
  extends RadixAccordionItemProps, AccordionItemVariants {}

const AccordionItem = forwardRef<AccordionItemRef, AccordionItemProps>(
  ({ className, ...props }, ref) => (
    <RadixAccordionItem
      {...props}
      className={accordionItemVariants({ className })}
      ref={ref}
    />
  ),
);

AccordionItem.displayName = "AccordionItem";

export { AccordionItem };

// -----------------------------------------------------------------------------

export type AccordionHeadingRef = React.ComponentRef<
  typeof RadixAccordionHeader
>;

export interface AccordionHeadingProps
  extends Omit<RadixAccordionHeaderProps, "asChild">, AccordionHeadingVariants {
  as?: "h2" | "h3" | "h4" | "h5" | "h6";
}

const AccordionHeading = forwardRef<AccordionHeadingRef, AccordionHeadingProps>(
  ({ as, children, className, ...props }, ref) => {
    const Comp = as ?? "h2";
    return (
      <RadixAccordionHeader
        {...props}
        asChild
        className={accordionHeadingVariants({ className })}
        ref={ref}
      >
        <Comp>{children}</Comp>
      </RadixAccordionHeader>
    );
  },
);

AccordionHeading.displayName = "AccordionHeading";

export { AccordionHeading };

// -----------------------------------------------------------------------------

export type AccordionTriggerRef = React.ComponentRef<
  typeof RadixAccordionTrigger
>;

export interface AccordionTriggerProps
  extends RadixAccordionTriggerProps, AccordionTriggerVariants {}

const AccordionTrigger = forwardRef<AccordionTriggerRef, AccordionTriggerProps>(
  ({ className, iconPalette, iconPlacement, palette, size, ...props }, ref) => (
    <RadixAccordionTrigger
      {...props}
      className={accordionTriggerVariants({
        className,
        iconPalette,
        iconPlacement,
        palette,
        size,
      })}
      ref={ref}
    />
  ),
);

AccordionTrigger.displayName = "AccordionTrigger";

export { AccordionTrigger };

// -----------------------------------------------------------------------------

export type AccordionIconRef = IconRef;

export interface AccordionIconProps
  extends Omit<IconProps, "name">, AccordionIconVariants {}

const AccordionIcon = forwardRef<AccordionIconRef, AccordionIconProps>(
  ({ className, ...props }, ref) => (
    <Icon
      {...props}
      name="ms-expand-more"
      className={accordionIconVariants({ className })}
      ref={ref}
    />
  ),
);

AccordionIcon.displayName = "AccordionIcon";

export { AccordionIcon };

// -----------------------------------------------------------------------------

export type AccordionContentRef = React.ComponentRef<
  typeof RadixAccordionContent
>;

export interface AccordionContentProps
  extends RadixAccordionContentProps, AccordionContentVariants {}

const AccordionContent = forwardRef<AccordionContentRef, AccordionContentProps>(
  ({ children, className, indent, size, ...props }, ref) => (
    <RadixAccordionContent
      {...props}
      className={accordionContentVariants({ className, indent, size })}
      ref={ref}
    >
      <div>{children}</div>
    </RadixAccordionContent>
  ),
);

AccordionContent.displayName = "AccordionContent";

export { AccordionContent };
